import i18n from "../i18n";
import axios from "axios";
import { getAnonymousSessionId, ANON_SESSION_HEADER } from "./anonymous";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
// Si REACT_APP_BACKEND_URL n'est pas défini (ex. déploiement OVH same-origin),
// on utilise "/api" relatif au lieu de produire "undefined/api".
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

export const api = axios.create({ baseURL: API });

// Public secret pages must work same-origin on custom domains
// (https://custom-domain.example/secret/ID -> /api/secrets/ID).
// - Sur le domaine plateforme (ou localhost) : on utilise l'URL backend configurée.
// - Sur un domaine personnalisé : on utilise /api relatif (même origine).
function resolvePublicBase() {
  try {
    const origin = window.location.origin;
    const host = window.location.hostname;
    const backendOrigin = BACKEND_URL ? new URL(BACKEND_URL).origin : null;
    if (origin === backendOrigin || host === "localhost" || host === "127.0.0.1") {
      return API;
    }
  } catch (e) {
    /* noop */
  }
  return "/api";
}

export const publicApi = axios.create({ baseURL: resolvePublicBase() });

const USER_TOKEN_KEY = "securepass_user_token";
const ACTIVE_WS_KEY = "securepass_active_workspace";

export const getUserToken = () => localStorage.getItem(USER_TOKEN_KEY);
export const setUserToken = (t) => localStorage.setItem(USER_TOKEN_KEY, t);
export const clearUserToken = () => localStorage.removeItem(USER_TOKEN_KEY);

export const getActiveWorkspaceId = () => localStorage.getItem(ACTIVE_WS_KEY);
export const setActiveWorkspaceId = (id) => {
  if (id) localStorage.setItem(ACTIVE_WS_KEY, id);
  else localStorage.removeItem(ACTIVE_WS_KEY);
};
export const clearActiveWorkspaceId = () => localStorage.removeItem(ACTIVE_WS_KEY);

api.interceptors.request.use((config) => {
  const token = getUserToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    // Session anonyme envoyée UNIQUEMENT hors authentification (anti-abus usage sans compte).
    try {
      config.headers[ANON_SESSION_HEADER] = getAnonymousSessionId();
    } catch (e) {
      /* noop */
    }
  }
  const wsId = getActiveWorkspaceId();
  if (wsId) config.headers["X-Workspace-Id"] = wsId;
  return config;
});

// Couche de compatibilité TRANSITOIRE : le backend émet encore des messages FR bruts.
// Résolution : 1) code stable  2) message FR exact -> clé  3) motif FR paramétré  4) générique localisé.
const ERR_NS = "common:apiErrors";
const SOURCE_LOCALE = "fr";

// Messages backend construits par f-string : regex sur le texte FR source -> clé + variables.
const ERROR_PATTERNS = [
  [/^Trop de tentatives\. Réessayez dans (\d+) minute\(s\)\.$/, "tooManyAttemptsRetryIn", ["n"]],
  [/^Quota mensuel maximal atteint \((\d+)\)\./, "monthlyQuotaReached", ["limit"]],
  [/^Vous ne pouvez pas réutiliser l'un de vos (\d+) derniers mots de passe\.$/, "passwordReuse", ["n"]],
  [/^Vous avez atteint votre quota de (\d+) demandes sécurisées ce mois-ci\.$/, "secureRequestQuota", ["limit"]],
  [/^Capacité maximale atteinte \((\d+)\/(\d+) sièges\)/, "seatCapacityUsed", ["used", "capacity"]],
  [/^Capacité maximale atteinte \((\d+) sièges, (\d+) utilisés\/réservés\).*avant de rattacher/, "seatCapacityReservedAttach", ["capacity", "used"]],
  [/^Capacité maximale atteinte \((\d+) sièges, (\d+) utilisés\/réservés\)/, "seatCapacityReserved", ["capacity", "used"]],
  [/^Votre offre inclut (\d+) sièges au minimum\.$/, "seatsMinimum", ["n"]],
  [/^Impossible : (\d+) sièges sont utilisés\./, "seatsInUse", ["n"]],
  [/^Le mot de passe doit contenir au moins (\d+) caractères\.$/, "passwordMinLength", ["n"]],
  [/^(Tarification (commerciale )?(indisponible|incomplète)|Aucun catalogue commercial actif|Région '.*' absente|Plan '.*' absent)/, "pricingUnavailable", []],
  [/^Pays ISO inconnus : (.+)$/, "unknownCountries", ["list"]],
  [/^Version inconnue pour le plan (\S+) : (.+)$/, "unknownPlanVersion", ["plan", "version"]],
  [/^Plan inconnu : (.+)$/, "unknownPlan", ["plan"]],
];

let frIndex = null;
function sourceIndex() {
  if (!frIndex) {
    const msgs = i18n.getResource(SOURCE_LOCALE, "common", "apiErrors.messages") || {};
    frIndex = new Map(Object.entries(msgs).map(([k, v]) => [v, k]));
  }
  return frIndex;
}

const genericError = () => i18n.t(`${ERR_NS}.generic`);

export function translateBackendMessage(text) {
  if (typeof text !== "string" || !text) return null;
  const key = sourceIndex().get(text.trim());
  if (key) return i18n.t(`${ERR_NS}.messages.${key}`);
  for (const [re, k, names] of ERROR_PATTERNS) {
    const m = text.match(re);
    if (m) return i18n.t(`${ERR_NS}.patterns.${k}`, Object.fromEntries(names.map((n, i) => [n, m[i + 1]])));
  }
  return null;
}

export function apiError(detail) {
  if (detail == null) return genericError();
  if (typeof detail === "string") return translateBackendMessage(detail) || genericError();
  if (Array.isArray(detail)) return i18n.t(`${ERR_NS}.validation`);
  if (typeof detail === "object") {
    if (detail.code && i18n.exists(`${ERR_NS}.codes.${detail.code}`)) return i18n.t(`${ERR_NS}.codes.${detail.code}`);
    return translateBackendMessage(detail.message) || translateBackendMessage(detail.msg) || genericError();
  }
  return genericError();
}
